import React, {PureComponent} from "react"
import setIfNotSet from "@irati/utils/setIfNotSet"
import client, {pagination, emptyList} from "@irati/utils/services/apollo"
import RefetchOnMount, {RefetchState} from "@irati/utils/services/apollo/utils/RefetchOnMount"
import Query, {QueryComponentProps} from "@irati/utils/services/apollo/Query"
import {ApolloState} from "@irati/utils/services/apollo/apolloStateInterface"
import {UserList, User} from "@irati/types"
import {QueryUsersForBusinessArgs} from "@irati/types/ops"
import usersQuery, {usersForBusinessKey} from "../../services/queries/usersForBusiness"

export const query = usersQuery

// can't export type from "../../services/queries/usersForBusiness" due to RN, should change to that when it does support
export interface UsersForBusinessVariables extends QueryUsersForBusinessArgs {
  includeBusiness?: boolean,
  includeIntegrations?: boolean
}

export function handleActivationStatusChange(variables: UsersForBusinessVariables, user: User) {
  if (
    (variables.enabled === true && !user.isEnabled) ||
    (variables.enabled === false && user.isEnabled)
  ) {
    removeFromUserList(variables, user)
  }
}

export function removeFromUserList(variables: UsersForBusinessVariables, user: User) {
  try {
    const data = client.readQuery({query, variables})
    const users = userList({data})
    const updatedList = users.results.filter((t) => t.uuid !== user.uuid)
    client.writeQuery({query, variables, data: {[usersForBusinessKey]: {...users, results: updatedList}}})
  } catch (e) {
    console.error("[DealList]: Failed to update cache")
  }
}

export function userList(apollo: Pick<ApolloState, "data">): UserList {
  return apollo.data?.[usersForBusinessKey] || emptyList()
}

export async function getPage(apollo: ApolloState, variables: UsersForBusinessVariables, pageIndex: number) {
  const {offset = 0} = userList(apollo)
  if (offset === 0 || offset / variables.limit === pageIndex) { // have page?
    return getNextPage(apollo, variables)
  }
}

export function getNextPage(apollo: ApolloState, variables: UsersForBusinessVariables) {
  const {results, total} = userList(apollo)
  if (results.length < total) { // expect more?
    return apollo.nextPage({includeBusiness: variables.includeBusiness || false, ...variables})
  }
}

interface Props extends Omit<QueryComponentProps<{usersForBusiness: UserList}, UsersForBusinessVariables>, "query"> {}

interface State extends RefetchState {}

export default class LoadUsersQuery extends PureComponent<Props, State> {
  refetch = new RefetchOnMount(this)

  static getDerivedStateFromProps(props: Props, state: State) {
    return RefetchOnMount.onUpdate(props, state)
  }

  componentDidMount = () => {
    // Disable until we remove the refetches in parent components (this will happen when user profile popup dies)
    // this.refetch.didMount()
  }

  addDefaults = (variables?: UsersForBusinessVariables) => {
    setIfNotSet(variables, "includeBusiness", false)
    setIfNotSet(variables, "includeIntegrations", false)
    return variables
  }

  render() {
    const {children, ...props} = this.props
    const variables = this.addDefaults(this.props.variables)

    return (
      <Query {...props} variables={variables} query={query}>
        {(apollo: ApolloState) => {
          const nextPage = (nextPageVariables: UsersForBusinessVariables) => {
            pagination({query, variables: this.addDefaults(nextPageVariables)}, apollo)
          }
          return children({...apollo, nextPage})
        }}
      </Query>
    )
  }
}
